import { useState } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, GraduationCap, Users, Building,
  Clock, Award, BookOpen, Target, Lightbulb
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TrainingPathway = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    role: "",
    industry: "",
    experience_level: "",
    current_certifications: [],
    training_goals: [],
    delivery_preference: "",
    time_availability: ""
  });

  const roles = [
    { id: "executive", name: "Executive / Director", desc: "Strategic oversight, management commitment" },
    { id: "manager", name: "Quality/Compliance Manager", desc: "System ownership, implementation lead" },
    { id: "supervisor", name: "Supervisor / Team Lead", desc: "Operational oversight, team management" },
    { id: "auditor", name: "Internal Auditor", desc: "Audit planning and execution" },
    { id: "operator", name: "Operator / Technician", desc: "Operational execution, procedures" },
    { id: "consultant", name: "Consultant / Advisor", desc: "External advisory services" },
    { id: "hr_training", name: "HR / Training Manager", desc: "Staff development, competency" }
  ];

  const industries = [
    "Food Manufacturing",
    "Agriculture & Farming",
    "Hospitality & Food Service",
    "Other Manufacturing",
    "Service Organizations",
    "Government / Public Sector",
    "Healthcare"
  ];

  const experienceLevels = [
    { id: "beginner", name: "Beginner", desc: "New to management systems and compliance" },
    { id: "intermediate", name: "Intermediate", desc: "Some experience, ready to deepen knowledge" },
    { id: "advanced", name: "Advanced", desc: "Experienced, seeking expert-level skills" }
  ];

  const trainingGoals = [
    { id: "awareness", name: "General Awareness", desc: "Understanding standards and requirements" },
    { id: "implementation", name: "System Implementation", desc: "Building and implementing systems" },
    { id: "auditing", name: "Internal Auditing", desc: "Conducting internal audits" },
    { id: "lead_auditing", name: "Lead Auditing", desc: "Leading audit teams" },
    { id: "certification", name: "Personal Certification", desc: "PECB or other professional credentials" },
    { id: "train_trainer", name: "Train the Trainer", desc: "Ability to train others" }
  ];

  const deliveryPreferences = [
    { id: "online_live", name: "Online Live", desc: "Interactive virtual sessions" },
    { id: "self_study", name: "Self-Study", desc: "Learn at your own pace" },
    { id: "in_person", name: "In-Person", desc: "Classroom training" },
    { id: "hybrid", name: "Hybrid", desc: "Mix of online and in-person" }
  ];

  const timeAvailability = [
    { id: "intensive", name: "Intensive (Full days)", desc: "Complete quickly" },
    { id: "part_time", name: "Part-Time (Half days)", desc: "Balance with work" },
    { id: "extended", name: "Extended (Evenings/Weekends)", desc: "Flexible schedule" }
  ];

  // Training recommendations database
  const trainingDatabase = {
    foundation: [
      { name: "ISO 9001:2026 Foundation", duration: "2 days", level: "Beginner", certification: "PECB" },
      { name: "ISO 22000 Foundation", duration: "2 days", level: "Beginner", certification: "PECB" },
      { name: "HACCP Foundation", duration: "1-2 days", level: "Beginner", certification: "Various" },
      { name: "ISO 27001 Foundation", duration: "2 days", level: "Beginner", certification: "PECB" }
    ],
    implementer: [
      { name: "ISO 9001 Lead Implementer", duration: "5 days", level: "Intermediate", certification: "PECB" },
      { name: "ISO 22000 Lead Implementer", duration: "5 days", level: "Intermediate", certification: "PECB" },
      { name: "ISO 27001 Lead Implementer", duration: "5 days", level: "Intermediate", certification: "PECB" }
    ],
    auditor: [
      { name: "ISO 9001 Internal Auditor", duration: "2 days", level: "Intermediate", certification: "PECB" },
      { name: "ISO 22000 Internal Auditor", duration: "2 days", level: "Intermediate", certification: "PECB" },
      { name: "ISO 9001 Lead Auditor", duration: "5 days", level: "Advanced", certification: "PECB" },
      { name: "ISO 22000 Lead Auditor", duration: "5 days", level: "Advanced", certification: "PECB" }
    ]
  };

  const toggleGoal = (goalId) => {
    setFormData(prev => ({
      ...prev,
      training_goals: prev.training_goals.includes(goalId)
        ? prev.training_goals.filter(g => g !== goalId)
        : [...prev.training_goals, goalId]
    }));
  };

  const generatePathway = () => {
    setIsLoading(true);
    
    setTimeout(async () => {
      const recommendations = [];
      const pathway = [];
      
      // Based on experience level and goals
      if (formData.experience_level === 'beginner' || formData.training_goals.includes('awareness')) {
        recommendations.push(...trainingDatabase.foundation.slice(0, 2));
      }
      
      if (formData.training_goals.includes('implementation')) {
        recommendations.push(...trainingDatabase.implementer.slice(0, 2));
      }
      
      if (formData.training_goals.includes('auditing') || formData.training_goals.includes('lead_auditing')) {
        if (formData.training_goals.includes('lead_auditing')) {
          recommendations.push(...trainingDatabase.auditor.filter(t => t.name.includes('Lead')));
        } else {
          recommendations.push(...trainingDatabase.auditor.filter(t => t.name.includes('Internal')));
        }
      }

      // Build pathway phases
      pathway.push({
        phase: 1,
        name: "Foundation",
        duration: "1-2 months",
        courses: trainingDatabase.foundation.slice(0, 2),
        objective: "Build foundational understanding of management systems"
      });

      if (formData.experience_level !== 'beginner') {
        pathway.push({
          phase: 2,
          name: "Specialization",
          duration: "2-3 months",
          courses: formData.training_goals.includes('auditing') 
            ? trainingDatabase.auditor.slice(0, 2)
            : trainingDatabase.implementer.slice(0, 2),
          objective: "Develop specialized skills in your focus area"
        });
      }

      if (formData.experience_level === 'advanced' || formData.training_goals.includes('lead_auditing')) {
        pathway.push({
          phase: 3,
          name: "Expert Level",
          duration: "3-6 months",
          courses: trainingDatabase.auditor.filter(t => t.level === 'Advanced'),
          objective: "Achieve professional certification and expert status"
        });
      }

      const pathwayResult = {
        id: `pathway-${Date.now()}`,
        role: formData.role,
        industry: formData.industry,
        experience_level: formData.experience_level,
        delivery_preference: formData.delivery_preference,
        recommended_courses: recommendations,
        pathway: pathway,
        total_duration: pathway.length <= 1 ? "1-2 months" : pathway.length === 2 ? "3-5 months" : "6-12 months",
        next_steps: [
          "Review recommended courses and select your starting point",
          "Consider the PECB certification pathway for professional credentials",
          "Discuss in-house training options if multiple staff need training",
          "Contact our team to discuss your specific training needs"
        ]
      };

      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        try {
          await axios.post(`${API}/tools/session/save`, {
            session_id: sessionId,
            tool_name: "training_pathway",
            result: pathwayResult
          });
        } catch (e) {
          console.error("Failed to save session:", e);
        }
      }

      setResult(pathwayResult);
      setIsLoading(false);
    }, 1500);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is your role?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This helps us recommend role-appropriate training.
              </p>
            </div>
            <div className="space-y-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => { setFormData({ ...formData, role: role.id }); setStep(2); }}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-brand ${
                    formData.role === role.id ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-slate-400" />
                    <div>
                      <div className="font-medium">{role.name}</div>
                      <div className="text-xs text-muted-foreground">{role.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What&apos;s your experience level?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                We&apos;ll match training to your current knowledge level.
              </p>
            </div>
            <div className="space-y-3">
              {experienceLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setFormData({ ...formData, experience_level: level.id })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.experience_level === level.id 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="font-medium">{level.name}</div>
                  <div className="text-sm text-muted-foreground">{level.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={!formData.experience_level}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What are your training goals?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all that apply to build your personalized pathway.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {trainingGoals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => toggleGoal(goal.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.training_goals.includes(goal.id) 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      formData.training_goals.includes(goal.id) 
                        ? 'bg-brand border-brand' 
                        : 'border-slate-300'
                    }`}>
                      {formData.training_goals.includes(goal.id) && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{goal.name}</div>
                      <div className="text-xs text-muted-foreground">{goal.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={formData.training_goals.length === 0}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Delivery Preferences</h3>
              <p className="text-sm text-muted-foreground mb-4">
                How would you prefer to receive training?
              </p>
            </div>
            
            {/* Delivery Format */}
            <div>
              <label className="text-sm font-medium mb-2 block">Preferred Format</label>
              <div className="grid grid-cols-2 gap-3">
                {deliveryPreferences.map((pref) => (
                  <button
                    key={pref.id}
                    onClick={() => setFormData({ ...formData, delivery_preference: pref.id })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.delivery_preference === pref.id 
                        ? 'border-brand bg-brand/5' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    <div className="font-medium text-sm">{pref.name}</div>
                    <div className="text-xs text-muted-foreground">{pref.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Industry */}
            <div>
              <label className="text-sm font-medium mb-2 block">Your Industry</label>
              <div className="flex flex-wrap gap-2">
                {industries.map((ind) => (
                  <button
                    key={ind}
                    onClick={() => setFormData({ ...formData, industry: ind })}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-all ${
                      formData.industry === ind
                        ? 'bg-brand text-white border-brand'
                        : 'border-slate-200 hover:border-brand'
                    }`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={generatePathway} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading || !formData.delivery_preference}
              >
                {isLoading ? "Building Pathway..." : "Generate Pathway"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Your Training Pathway</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Summary */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="h-5 w-5 text-brand" />
            <span className="font-semibold">Pathway Summary</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Role:</span>
              <span className="ml-2 font-medium">{roles.find(r => r.id === result.role)?.name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Level:</span>
              <span className="ml-2 font-medium capitalize">{result.experience_level}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Format:</span>
              <span className="ml-2 font-medium capitalize">{result.delivery_preference?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Est. Duration:</span>
              <span className="ml-2 font-bold text-brand">{result.total_duration}</span>
            </div>
          </div>
        </div>

        {/* Pathway Phases */}
        <div className="mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-brand" />
            Your Learning Journey
          </h3>
          <div className="space-y-4">
            {result.pathway.map((phase, index) => (
              <div key={index} className="relative pl-8">
                <div className="absolute left-0 top-0 w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                  {phase.phase}
                </div>
                {index < result.pathway.length - 1 && (
                  <div className="absolute left-3 top-6 w-0.5 h-full bg-brand/20" />
                )}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{phase.name}</span>
                    <Badge variant="outline" className="text-xs">{phase.duration}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{phase.objective}</p>
                  <div className="space-y-2">
                    {phase.courses.map((course, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-white rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-brand" />
                          <span>{course.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{course.duration}</span>
                          {course.certification && (
                            <Badge className="bg-brand/10 text-brand text-xs">{course.certification}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-brand" />
            Next Steps
          </h3>
          <ul className="space-y-2">
            {result.next_steps.map((step, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="font-bold text-brand">{i + 1}.</span> {step}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request Training Info
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">Training Pathway Builder</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 4</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default TrainingPathway;
